import { useEffect, useState } from "react";
import {
  DialogActions,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import MDDialog from "components/MDDialog";
import MDSnackbar from "components/MDSnackbar";
import CircularIndeterminate from "components/MDLoading";
import MDButton from "components/MDButton";
import MDTextButton from "components/MDTextButton";
import { useMaterialUIController } from "context";
import { useDispatch, useSelector } from "react-redux";
import { getSubscriptionChargeDetail } from "store/slice/subscriptions/subscriptionSlice";
import { get, isEmpty } from "lodash";
import MDTypography from "components/MDTypography";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { getSubscriptionDuration } from "global/functions";
import { getTimezoneName } from "global/functions";
import MDBox from "components/MDBox";
import { chargeSubscription } from "store/slice/subscriptions/subscriptionSlice";
import ConfirmationDialog from "components/UIComponents/ConfirmationDialog";
import { getSubscriptionsByPlaceId } from "store/slice/subscriptions/subscriptionSlice";
import { getSubscriptionDetail } from "store/slice/subscriptions/subscriptionSlice";

const SubscriptionChargeDialog = (props) => {
  const [controller] = useMaterialUIController();
  const dispatch = useDispatch();
  const { sidenavColor } = controller;
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { dialogOpen, onClose, subscriptionData } = props;
  const loading = useSelector((state) => state.subscriptions.loading);
  const chargeData = useSelector(
    (state) => state.subscriptions.subscriptionChargeData || [],
  );

  const isReactive = get(subscriptionData, "isReactivationRequested", false);

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  useEffect(() => {
    if (!isEmpty(subscriptionData)) {
      dispatch(getSubscriptionChargeDetail(subscriptionData._id));
    }
  }, [dispatch, subscriptionData._id]);

  const handelCharge = () => {
    setIsLoading(true);
    const payload = {
      selectedMonths: selectedRows,
      renewMonths: selectedRows.length,
      totalAmount: calculateSummary().grandTotal,
      isReactive,
    };
    dispatch(
      chargeSubscription({ subscriptionId: subscriptionData._id, ...payload }),
    )
      .unwrap()
      .then(async (res) => {
        const success = res?.success;
        if (success) {
          await dispatch(
            getSubscriptionsByPlaceId(subscriptionData?.placeId?._id),
          );
          await dispatch(getSubscriptionChargeDetail(subscriptionData._id));
          await dispatch(getSubscriptionDetail(subscriptionData._id));
          setConfirmDialogOpen(false);
          onClose();
        }
        setIsLoading(false);
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
        setConfirmDialogOpen(false);
        onClose();
      })
      .catch((err) => {
        setIsLoading(false);

        const errorMessage =
          err?.payload?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong!";

        setNotification((prev) => ({
          ...prev,
          color: "error",
          title: "Error",
          content: errorMessage,
          icon: "warning",
          show: true,
        }));
        setConfirmDialogOpen(false);
        onClose();
        console.error("Charge API Error:", err);
      });
  };

  const columnsData = [
    {
      field: "renewMonth",
      headerName: "Duration Month",
      width: 150,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${row.month.join(" To ") || "-"}`}
        </MDTypography>
      ),
    },

    {
      field: "duration",
      headerName: "Duration",
      width: 200,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography display="block" variant="caption">
          {getSubscriptionDuration({
            startDate: row.startDate,
            endDate: row.endDate,
            tz: row?.placeId?.timeZoneId || getTimezoneName(),
          })}
        </MDTypography>
      ),
    },

    {
      field: "totalAmount",
      headerName: "Total Amount",
      width: 100,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${row?.totalAmount.toFixed(2)}`}
        </MDTypography>
      ),
    },
  ];
  const handleSelectionChange = (selectionModel) => {
    const selectedData = chargeData.filter((row) =>
      selectionModel.includes(row.index),
    );
    setSelectedRows(selectedData);
  };

  const getRowClassName = (params) => {
    return "";
  };

  const isRowSelectable = () => true;

  const calculateSummary = () => {
    if (!selectedRows.length)
      return {
        grandTotal: 0,
      };

    return {
      grandTotal: selectedRows.reduce(
        (acc, row) => acc + (row.totalAmount || 0),
        0,
      ),
    };
  };

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..! "
        open={isLoading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDDialog
        dialogTitle={`Subscription ${
          subscriptionData.isReactivationRequested ? "Reactivation" : "Charge"
        } Confirmation`}
        open={dialogOpen}
        dialogClose={() => {
          setSelectedRows([]);
          onClose();
        }}
        closeIcon={true}
        maxWidth="sm"
      >
        <DialogContent>
          <MDBox bgcolor="grey.100" borderRadius={2}>
            <Typography variant="h6">Subscription Details -</Typography>
            <Stack direction="row" flexWrap="wrap">
              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>Subscription Number: </strong>
                {subscriptionData.subscriptionNumber || "-"}
              </Typography>
              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>Subscription Type : </strong>
                {subscriptionData.isMonthly ? "Monthly" : "Custom" || "-"}
              </Typography>
              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>Payment Method : </strong>
                {get(subscriptionData, "paymentMethodType", "NA") === "card"
                  ? "Credit Card"
                  : subscriptionData.paymentMethodType}
              </Typography>
              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>License Plates : </strong>
                {subscriptionData.licensePlate
                  ?.map((plate) => plate.licensePlateNumber)
                  .join(", ") || "-"}
              </Typography>

              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>Name : </strong>
                {`${get(subscriptionData, "customerId.firstName", "NA")} ${get(
                  subscriptionData,
                  "customerId.lastName",
                  "NA",
                )}`}
              </Typography>
              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>Email : </strong>
                {get(subscriptionData, "customerId.email", "NA")}
              </Typography>
              <Typography variant="body2" fontSize="small" mr={2}>
                <strong>Cellphone : </strong>
                {get(subscriptionData, "customerId.isEmailPrimary", false)
                  ? get(subscriptionData, "customerId.secondaryMobile", "N/A")
                  : get(subscriptionData, "customerId.mobile", "N/A")}
              </Typography>
            </Stack>
          </MDBox>

          {!isEmpty(chargeData) && (
            <MDBox mt={2}>
              <Typography variant="caption">
                *Please select subscription month which you want to pay for.
              </Typography>
              <MDDataGrid
                rows={!isEmpty(chargeData) ? chargeData : []}
                columns={columnsData}
                getRowId={(row) => row.index}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
                loading={loading}
                components={{
                  NoRowsOverlay: EmptyTableRowOverlay,
                  LoadingOverlay: LinearProgress,
                }}
                // autoHeight
                checkboxSelection
                onRowSelectionModelChange={handleSelectionChange}
                getRowClassName={getRowClassName}
                isRowSelectable={isRowSelectable}
                selectionModel={selectedRows}
              />
            </MDBox>
          )}

          <MDBox mt={2} p={2} bgcolor="grey.100" borderRadius={2}>
            <Typography variant="h6">Payment Summary</Typography>
            <Stack direction="row" spacing={4}>
              <Typography variant="body2" fontSize="small">
                <strong>Amount To Pay : </strong> $
                {calculateSummary().grandTotal.toFixed(2)}
              </Typography>
            </Stack>
            <Typography mt={2} variant="caption">
              * Review and Confirm Subscription Months Before Charge Payment
            </Typography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="end"
          >
            <MDTextButton
              onClick={() => {
                setSelectedRows([]);
                onClose();
              }}
            >
              Cancel
            </MDTextButton>
            <MDButton
              size="small"
              variant="contained"
              color={sidenavColor}
              onClick={() => setConfirmDialogOpen(true)}
              disabled={selectedRows.length === 0 && !isReactive}
            >
              {`${isReactive ? "Reactivate" : "Pay"} ${
                selectedRows.length === 0 && isReactive
                  ? ""
                  : `$${calculateSummary().grandTotal.toFixed(2)}`
              } `}
            </MDButton>
          </Stack>
        </DialogActions>
      </MDDialog>
      <ConfirmationDialog
        title={
          isReactive ? "Reactivation Confirmation!" : "Payment Confirmation!"
        }
        description={`Are you sure you want to ${
          isReactive ? "Reactive" : "charge"
        } this subscription?`}
        dialogOpen={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
        }}
        handelClick={handelCharge}
      />
    </>
  );
};

export default SubscriptionChargeDialog;
